import type { CodeModuleLanguage } from './codeMirrorLanguageExtensions';

export type ResolvedRevisionFileContentView =
    | { mode: 'json-tree'; value: object }
    | { mode: 'codemirror'; language: CodeModuleLanguage; content: string };

const EXT_TO_LANGUAGE: [string, CodeModuleLanguage][] = [
    ['.tsx', 'tsx'],
    ['.mts', 'typescript'],
    ['.cts', 'typescript'],
    ['.ts', 'typescript'],
    ['.jsx', 'jsx'],
    ['.mjs', 'javascript'],
    ['.cjs', 'javascript'],
    ['.js', 'javascript'],
    ['.pyw', 'python'],
    ['.pyi', 'python'],
    ['.py', 'python'],
    ['.json', 'json'],
    ['.jsonc', 'json'],
];

function languageFromPath(filePath: string): CodeModuleLanguage | null {
    const lower = filePath.toLowerCase();
    for (const [ext, lang] of EXT_TO_LANGUAGE) {
        if (lower.endsWith(ext)) {
            return lang;
        }
    }
    return null;
}

function tryParseJsonObjectOrArray(text: string): object | null {
    const t = text.trim();
    if (!t) {
        return null;
    }
    try {
        const v = JSON.parse(text) as unknown;
        if (v !== null && typeof v === 'object') {
            return v as object;
        }
        return null;
    } catch {
        return null;
    }
}

function looksLikeJsonArtifact(params: { filePath: string; contentType: string }): boolean {
    const { filePath, contentType } = params;
    const mime = contentType.toLowerCase();
    if (mime.includes('json')) {
        return true;
    }
    const lower = filePath.toLowerCase();
    return lower.endsWith('.json') || lower.endsWith('.jsonc');
}

function languageFromContentType(contentType: string): CodeModuleLanguage | null {
    const mime = contentType.toLowerCase();
    if (mime.includes('typescript')) {
        return 'typescript';
    }
    if (mime.includes('javascript')) {
        return 'javascript';
    }
    if (mime.includes('x-python') || mime.includes('python')) {
        return 'python';
    }
    return null;
}

export function resolveRevisionFileContentView(params: {
    filePath: string;
    contentType: string;
    content: string;
}): ResolvedRevisionFileContentView {
    const { filePath, contentType, content } = params;

    if (looksLikeJsonArtifact({ filePath, contentType })) {
        const parsed = tryParseJsonObjectOrArray(content);
        if (parsed !== null) {
            return { mode: 'json-tree', value: parsed };
        }
        return { mode: 'codemirror', language: 'json', content };
    }

    const fromPath = languageFromPath(filePath);
    if (fromPath !== null && fromPath !== 'json') {
        return { mode: 'codemirror', language: fromPath, content };
    }

    const fromMime = languageFromContentType(contentType);
    if (fromMime !== null) {
        return { mode: 'codemirror', language: fromMime, content };
    }

    return { mode: 'codemirror', language: 'plain', content };
}
